import {
  AnalyticsFingerprintField,
  CampaignEdit,
  CampaignMappingStatus,
  CampaignOptionsResponse,
  CampaignMappingRow,
  CampaignSavePayloadItem,
  MappingLevel,
  MappingOption,
} from "@blisspointmedia/bpm-types/dist/CampaignMappingTool";

const toOptionalString = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const next = String(value).trim();
  return next === "" ? undefined : next;
};

const normalizeStatus = (value: unknown): CampaignMappingStatus | undefined => {
  const normalized = toOptionalString(value)?.toLowerCase();
  if (normalized === "labelled" || normalized === "unlabelled") {
    return normalized;
  }
  return undefined;
};

export const deriveStatus = (row: Record<string, unknown>): CampaignMappingStatus => {
  const statusFromCsv = normalizeStatus(row.status);
  if (statusFromCsv) {
    return statusFromCsv;
  }

  const analyticsSegmentationId = toOptionalString(row.analytics_segmentation_id);
  const mappedCampaignId = toOptionalString(row.mapped_campaign_id);
  const mappedAdGroupId = toOptionalString(row.mapped_ad_group_id);
  const mappedAdId = toOptionalString(row.mapped_ad_id);

  return analyticsSegmentationId || mappedCampaignId || mappedAdGroupId || mappedAdId
    ? "labelled"
    : "unlabelled";
};

export const normalizeCampaignMappingRow = (row: Record<string, unknown>): CampaignMappingRow => {
  return {
    raw_data_id: toOptionalString(row.raw_data_id) || "",
    status: deriveStatus(row),
    analytics_segmentation_id: toOptionalString(row.analytics_segmentation_id),
    analytics_source: toOptionalString(row.analytics_source),
    analytics_raw_source: toOptionalString(row.analytics_raw_source),
    analytics_raw_medium: toOptionalString(row.analytics_raw_medium),
    analytics_campaign_name: toOptionalString(row.analytics_campaign_name),
    analytics_campaign_id: toOptionalString(row.analytics_campaign_id),
    analytics_ad_group_name: toOptionalString(row.analytics_ad_group_name),
    analytics_ad_group_id: toOptionalString(row.analytics_ad_group_id),
    analytics_ad_name: toOptionalString(row.analytics_ad_name),
    analytics_ad_id: toOptionalString(row.analytics_ad_id),
    mapped_campaign_id: toOptionalString(row.mapped_campaign_id),
    mapped_ad_group_id: toOptionalString(row.mapped_ad_group_id),
    mapped_ad_id: toOptionalString(row.mapped_ad_id),
    mapped_campaign_source: toOptionalString(row.mapped_campaign_source),
    mapped_account_id: toOptionalString(row.mapped_account_id),
    company: toOptionalString(row.company),
  };
};

export const ANALYTICS_FINGERPRINT_FIELDS = [
  "analytics_source",
  "analytics_raw_source",
  "analytics_raw_medium",
  "analytics_campaign_name",
  "analytics_campaign_id",
  "analytics_ad_group_name",
  "analytics_ad_group_id",
  "analytics_ad_name",
  "analytics_ad_id",
  "analytics_segmentation_id",
] as const;

export const MAPPING_LEVELS: readonly MappingLevel[] = ["campaign", "ad_group", "ad"] as const;

const LEVEL_FIELD_PREFIX: Record<MappingLevel, "campaign" | "ad_group" | "ad"> = {
  campaign: "campaign",
  ad_group: "ad_group",
  ad: "ad",
};

export const MAPPED_ID_FIELD_BY_LEVEL: Record<
  MappingLevel,
  "mapped_campaign_id" | "mapped_ad_group_id" | "mapped_ad_id"
> = {
  campaign: "mapped_campaign_id",
  ad_group: "mapped_ad_group_id",
  ad: "mapped_ad_id",
};

const LEVEL_LABEL: Record<MappingLevel, string> = {
  campaign: "Campaign",
  ad_group: "Ad Group",
  ad: "Ad",
};

export const getLevelLabel = (level: MappingLevel): string => LEVEL_LABEL[level];

export const makeMappingOptionKey = (id: string, accountId: string, source: string): string =>
  `${id}|${accountId}|${source}`;

export const ANALYTICS_FINGERPRINT_COLS_BY_LEVEL: Record<
  MappingLevel,
  readonly AnalyticsFingerprintField[]
> = {
  campaign: [
    "analytics_source",
    "analytics_raw_source",
    "analytics_raw_medium",
    "analytics_campaign_name",
    "analytics_campaign_id",
    "analytics_segmentation_id",
  ],
  ad_group: [
    "analytics_source",
    "analytics_raw_source",
    "analytics_raw_medium",
    "analytics_campaign_name",
    "analytics_campaign_id",
    "analytics_ad_group_name",
    "analytics_ad_group_id",
    "analytics_segmentation_id",
  ],
  ad: [...ANALYTICS_FINGERPRINT_FIELDS],
};

export const buildOptionIndexByLevel = (
  mappingOptions: Record<MappingLevel, MappingOption[]>
): Record<MappingLevel, Map<string, MappingOption>> =>
  MAPPING_LEVELS.reduce((acc, level) => {
    const map = new Map<string, MappingOption>();
    for (const option of mappingOptions[level]) {
      const key = makeMappingOptionKey(option.id, option.accountId, option.source);
      if (!map.has(key)) {
        map.set(key, option);
      }
    }
    acc[level] = map;
    return acc;
  }, {} as Record<MappingLevel, Map<string, MappingOption>>);

const buildAnalyticsFingerprintPayload = (
  row: CampaignMappingRow
): Record<AnalyticsFingerprintField, string> =>
  ANALYTICS_FINGERPRINT_FIELDS.reduce((acc, field) => {
    acc[field] = row[field] ?? "";
    return acc;
  }, {} as Record<AnalyticsFingerprintField, string>);

const synthesizeOptionFromMappedRow = (
  row: CampaignMappingRow,
  level: MappingLevel,
  optionByKeyByLevel: Record<MappingLevel, Map<string, MappingOption>>
): MappingOption | null => {
  const mappedId = row[MAPPED_ID_FIELD_BY_LEVEL[level]];
  if (!mappedId) {
    return null;
  }
  const accountId = row.mapped_account_id || "";
  const source = row.mapped_campaign_source || "";
  const key = makeMappingOptionKey(mappedId, accountId, source);
  return (
    optionByKeyByLevel[level].get(key) || {
      value: key,
      label: mappedId,
      level,
      id: mappedId,
      accountId,
      source,
    }
  );
};

export const getEffectiveSelection = (
  row: CampaignMappingRow,
  edit: CampaignEdit | undefined,
  level: MappingLevel,
  optionByKeyByLevel: Record<MappingLevel, Map<string, MappingOption>>
): MappingOption | null => {
  if (edit && level in edit) {
    return edit[level] ?? null;
  }
  return synthesizeOptionFromMappedRow(row, level, optionByKeyByLevel);
};

export const getEffectiveAccountSource = (
  row: CampaignMappingRow,
  edit: CampaignEdit | undefined,
  optionByKeyByLevel: Record<MappingLevel, Map<string, MappingOption>>,
  excludeLevel?: MappingLevel
): { accountId: string; source: string } | null => {
  for (const level of MAPPING_LEVELS) {
    if (level === excludeLevel) {
      continue;
    }
    const selection = getEffectiveSelection(row, edit, level, optionByKeyByLevel);
    if (selection) {
      return { accountId: selection.accountId, source: selection.source };
    }
  }
  return null;
};

const resolveLevel = (
  row: CampaignMappingRow,
  edit: CampaignEdit,
  level: MappingLevel
): { id: string; accountId: string; source: string } => {
  if (level in edit) {
    const slot = edit[level];
    if (slot) {
      return { id: slot.id, accountId: slot.accountId, source: slot.source };
    }
    return { id: "", accountId: "", source: "" };
  }
  return {
    id: row[MAPPED_ID_FIELD_BY_LEVEL[level]] || "",
    accountId: row.mapped_account_id || "",
    source: row.mapped_campaign_source || "",
  };
};

export const editHasChanges = (row: CampaignMappingRow, edit: CampaignEdit): boolean => {
  const existingAccountId = row.mapped_account_id || "";
  const existingSource = row.mapped_campaign_source || "";
  for (const level of MAPPING_LEVELS) {
    if (!(level in edit)) {
      continue;
    }
    const slot = edit[level];
    const existingId = row[MAPPED_ID_FIELD_BY_LEVEL[level]] || "";
    const newId = slot?.id || "";
    if (newId !== existingId) {
      return true;
    }
    if (slot && (slot.accountId !== existingAccountId || slot.source !== existingSource)) {
      return true;
    }
  }
  return false;
};

export const editClearedSlots = (row: CampaignMappingRow, edit: CampaignEdit): MappingLevel[] => {
  const cleared: MappingLevel[] = [];
  for (const level of MAPPING_LEVELS) {
    if (level in edit && edit[level] === null && row[MAPPED_ID_FIELD_BY_LEVEL[level]]) {
      cleared.push(level);
    }
  }
  return cleared;
};

export const getDispatchLevel = (
  row: CampaignMappingRow,
  edit: CampaignEdit
): MappingLevel | null => {
  if (resolveLevel(row, edit, "ad").id) {
    return "ad";
  }
  if (resolveLevel(row, edit, "ad_group").id) {
    return "ad_group";
  }
  if (resolveLevel(row, edit, "campaign").id) {
    return "campaign";
  }
  return null;
};

export const buildSavePayloadItem = (
  row: CampaignMappingRow,
  edit: CampaignEdit
): CampaignSavePayloadItem | null => {
  const campaign = resolveLevel(row, edit, "campaign");
  const adGroup = resolveLevel(row, edit, "ad_group");
  const ad = resolveLevel(row, edit, "ad");

  if (!campaign.id && !adGroup.id && !ad.id) {
    return null;
  }

  const accountSource = [ad, adGroup, campaign].find(slot => slot.id) || {
    accountId: row.mapped_account_id || "",
    source: row.mapped_campaign_source || "",
  };

  return {
    raw_data_id: edit.rawDataId,
    campaign_id: campaign.id,
    ad_group_id: adGroup.id,
    ad_id: ad.id,
    account_id: accountSource.accountId,
    source: accountSource.source,
    ...buildAnalyticsFingerprintPayload(row),
  };
};

export const estimateFanOut = (
  row: CampaignMappingRow,
  edit: CampaignEdit,
  allRows: readonly CampaignMappingRow[]
): number => {
  const level = getDispatchLevel(row, edit);
  if (!level) {
    return 0;
  }
  const cols = ANALYTICS_FINGERPRINT_COLS_BY_LEVEL[level];
  const target = cols.map(col => row[col] || "").join("");
  let count = 0;
  for (const candidate of allRows) {
    if (cols.map(col => candidate[col] || "").join("") === target) {
      count += 1;
    }
  }
  return count;
};

export const toMappingOptions = (
  rows: CampaignOptionsResponse[]
): Record<MappingLevel, MappingOption[]> => {
  const seenByLevel: Record<MappingLevel, Map<string, MappingOption>> = {
    campaign: new Map(),
    ad_group: new Map(),
    ad: new Map(),
  };

  for (const row of rows) {
    for (const level of MAPPING_LEVELS) {
      const prefix = LEVEL_FIELD_PREFIX[level];
      const id = row[`${prefix}_id` as keyof CampaignOptionsResponse];
      const name = row[`${prefix}_name` as keyof CampaignOptionsResponse];
      if (!id || !name) {
        continue;
      }
      const accountId = row.account_id ?? "";
      const source = row.source ?? "";
      const value = `${makeMappingOptionKey(id, accountId, source)}|${name}`;
      if (seenByLevel[level].has(value)) {
        continue;
      }
      seenByLevel[level].set(value, {
        value,
        label: name,
        level,
        id,
        accountId,
        source,
      });
    }
  }

  return MAPPING_LEVELS.reduce((acc, level) => {
    acc[level] = Array.from(seenByLevel[level].values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return acc;
  }, {} as Record<MappingLevel, MappingOption[]>);
};
